import proj4 from "proj4"
import { PdfDict, PdfDocument } from "./pdf-objects"

const NO_DATUM_SHIFT = "@null"
const NORTH_POLE = 90
const SOUTH_POLE = -90

const DATUM_DEFINITIONS = new Map([
    ["WGE", "+datum=WGS84"],
    ["WGS84", "+datum=WGS84"],
    ["NAR", "+datum=NAD83"],
    ["NAS", "+datum=NAD27"],
])

const readParameters = (doc: PdfDocument, dict: PdfDict) => ({
    number: (key: string) => doc.asNumber(doc.entry(dict, key)),
    text: (key: string) => doc.asText(doc.entry(dict, key)),
})

type ProjectionParameters = ReturnType<typeof readParameters>

const falseOrigin = (parameters: ProjectionParameters) =>
    `+x_0=${parameters.number("FalseEasting") ?? 0} +y_0=${parameters.number("FalseNorthing") ?? 0}`

const PROJECTION_DEFINITIONS = new Map<string, (parameters: ProjectionParameters) => string | null>(
    [
        ["GEOGRAPHIC", () => "+proj=longlat"],
        ["GEODETIC", () => "+proj=longlat"],
        [
            "UT",
            (parameters) => {
                const zone = parameters.number("Zone")
                if (zone === null) return null
                const south = parameters.text("Hemisphere") === "S" ? " +south" : ""
                return `+proj=utm +zone=${zone}${south} +units=m`
            },
        ],
        [
            "TC",
            (parameters) =>
                `+proj=tmerc +lat_0=${parameters.number("OriginLatitude") ?? 0}` +
                ` +lon_0=${parameters.number("CentralMeridian") ?? 0}` +
                ` +k_0=${parameters.number("ScaleFactor") ?? 1} ${falseOrigin(parameters)} +units=m`,
        ],
        [
            "LE",
            (parameters) => {
                const first = parameters.number("StandardParallelOne")
                const second = parameters.number("StandardParallelTwo")
                if (first === null || second === null) return null
                return (
                    `+proj=lcc +lat_1=${first} +lat_2=${second}` +
                    ` +lat_0=${parameters.number("OriginLatitude") ?? 0}` +
                    ` +lon_0=${parameters.number("CentralMeridian") ?? 0} ${falseOrigin(parameters)} +units=m`
                )
            },
        ],
        [
            "MC",
            (parameters) =>
                `+proj=merc +lat_ts=${parameters.number("OriginLatitude") ?? 0}` +
                ` +lon_0=${parameters.number("CentralMeridian") ?? 0}` +
                ` +k_0=${parameters.number("ScaleFactor") ?? 1} ${falseOrigin(parameters)} +units=m`,
        ],
        [
            "PG",
            (parameters) => {
                const trueScale = parameters.number("LatitudeTrueScale")
                if (trueScale === null) return null
                return (
                    `+proj=stere +lat_0=${trueScale < 0 ? SOUTH_POLE : NORTH_POLE} +lat_ts=${trueScale}` +
                    ` +lon_0=${parameters.number("LongitudeDownFromPole") ?? 0}` +
                    ` +k_0=${parameters.number("ScaleFactor") ?? 1} ${falseOrigin(parameters)} +units=m`
                )
            },
        ],
    ]
)

const readDatum = (doc: PdfDocument, dict: PdfDict) => {
    const datum = doc.entry(dict, "Datum")
    const code = doc.asText(datum)
    if (code !== null) return DATUM_DEFINITIONS.get(code.trim().toUpperCase()) ?? null

    const nested = doc.asDict(datum)
    if (!nested) return null
    const ellipsoid = doc.asDict(doc.entry(nested, "Ellipsoid"))
    if (!ellipsoid) return null

    const semiMajorAxis = doc.asNumber(doc.entry(ellipsoid, "SemiMajorAxis"))
    const inverseFlattening = doc.asNumber(doc.entry(ellipsoid, "InvFlattening"))
    if (semiMajorAxis === null) return null
    return inverseFlattening === null || inverseFlattening === 0
        ? `+a=${semiMajorAxis} +b=${semiMajorAxis}`
        : `+a=${semiMajorAxis} +rf=${inverseFlattening}`
}

const register = (definition: string) => {
    proj4.defs(definition, definition)
    proj4.defs(definition).nadgrids = NO_DATUM_SHIFT
    return definition
}

export const resolveProjection = (doc: PdfDocument, dict: PdfDict) => {
    const wkt = doc.asText(doc.entry(dict, "WKT"))
    if (wkt) return register(wkt)

    const projectionType = doc.asText(doc.entry(dict, "ProjectionType"))
    const build =
        projectionType === null ? undefined : PROJECTION_DEFINITIONS.get(projectionType.trim())
    if (!build) return null

    const projection = build(readParameters(doc, dict))
    const datum = readDatum(doc, dict)
    return projection && datum ? register(`${projection} ${datum} +no_defs`) : null
}

export const toProjected = (projection: string, longitude: number, latitude: number) =>
    proj4("WGS84", projection, [longitude, latitude])

export const toWgs84 = (projection: string, point: number[]) => proj4(projection, "WGS84", point)
